import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { enqueueSnackbar } from 'notistack';
import { Box, Grid2, Typography } from '@mui/material';

import AppConfig from '@/src/config/AppConfig';
import AttributesConfig, { IAttributeDef } from '@/src/config/AttributesConfig';
import DynamicBreadCrumbs from '@/src/components/common/DynamicBreadCrumbs';
import DynamicInput from '@/src/components/common/DynamicInput';
import CredentialDialogue from '@/src/components/CredentialDialogue';
import FormattedUILabel from '@/src/components/common/FormattedUILabel';
import SaveAndCancel from '@/src/components/common/SaveAndCancel';
import NoAuthorization from '@/src/components/common/NoAuthorization';
import { RootState } from '@/src/datastore/store';
import UseNavigation from '@/src/hooks/useNavigation';
import AppMessages from '@/src/config/message_config/AppMessages';
import AllAppLevelCredentialsService from '@/src/services/all-app-Level-Credentials/allAppLevelCredentialsService';
import SchemaValidations from '@/src/utils/SchemaValidations';
import lightTheme from '@/src/styles/themes/lightTheme';
import {
  IAttributeOptions,
  ILooseObject,
  INPUT_FIELD_TYPE_MAP,
  UI_TYPE_INPUT_TYPE_MAP
} from '@/src/types/common.interface';

const AddAppLevelCredentials = () => {
  const [extraFieldConfig, setExtraFieldConfig] = useState<ILooseObject>({});
  const [isOpenDialogue, setIsOpenDialogue] = useState<boolean>(false);
  const [credentialData, SetCredentialData] = useState<ILooseObject>({});
  const navigation = UseNavigation();

  const getAppPermissions = (app_key?: string) => {
    return Object.entries(userAppPermissions as Record<string, string[]>)?.reduce(
      (acc: string[], [key, permissions]: [string, string[]]) => {
        if (app_key) {
          if (key === app_key) {
            return Array.from(new Set([...acc, ...permissions]));
          } else {
            return acc;
          }
        }
        return Array.from(new Set([...acc, ...permissions]));
      },
      []
    );
  };

  const userAppPermissions: ILooseObject = useSelector(
    (state: RootState) => state?.userDetails?.OptionsAndPermissions?.appPermissions
  );

  const getAvailableOptions = (attributeField: ILooseObject, extraFieldConfig?: ILooseObject) => {
    const availableOptions =
      extraFieldConfig?.[attributeField?.name]?.options ?? attributeField?.options ?? [];
    return availableOptions;
  };

  const userDestinationApplicationsOptions: IAttributeOptions[] = Object.values(
    useSelector((state: RootState) => state?.userDetails?.OptionsAndPermissions)
      ?.applicationsOptions as IAttributeOptions[]
  )
    ?.map((application: IAttributeOptions) => application)
    ?.filter((application: IAttributeOptions) => {
      return getAppPermissions(application.value as string)?.includes('api_key_create');
    });

  const schema = SchemaValidations.AddAppLevelCredentials(
    AttributesConfig.CREATE_APP_LEVEL_CREDENTIALS_CONFIG_INPUT
  );

  const {
    control: addAppLevelCredentialsControl,
    getValues: getAppLevelCredentialsValues,
    handleSubmit: handleAddAppLevelCredentialsSubmit,
    reset: resetAddAppLevelCredentials,
    clearErrors: clearAddAppLevelCredentialsErrors,
    watch,
    formState: {
      errors: addAppLevelCredentialsErrors,
      isSubmitting: isAddAppLevelCredentialsSubmitting,
      isDirty: isAddAppLevelCredentialsDirty
    }
  } = useForm({
    defaultValues: AttributesConfig.CREATE_APP_LEVEL_CREDENTIALS_CONFIG_INPUT?.reduce(
      (acc: ILooseObject, attribute: IAttributeDef) => {
        if (attribute.name === 'role_keys') {
          acc[attribute.name] = [];
        } else {
          acc[attribute?.name] = '';
        }
        return acc;
      },
      {}
    ),
    resolver: yupResolver(schema, { abortEarly: false }),
    mode: 'onBlur'
  });
  const watchedValues = watch();

  const handleCloseCredentialDialogue = () => {
    setIsOpenDialogue(false);
    SetCredentialData({});
    navigation.navigateTo('/app-level-credentials');
  };

  const getRolesByDestinationApplication = async (destinationApp: string) => {
    try {
      const result = await AllAppLevelCredentialsService.getRolesListByDestinationApplications({
        applicationKeys: destinationApp
      });
      const roleOptions =
        result?.data?.data?.roles?.map((role: ILooseObject) => ({
          label: role.ui_label,
          value: role.role_key
        })) ?? [];

      setExtraFieldConfig((prev) => ({
        ...prev,
        role_keys: {
          ...prev?.role_keys,
          options: roleOptions
        }
      }));
    } catch (err) {
      enqueueSnackbar(err?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }
  };

  const getAllApplicationList = async () => {
    try {
      const result = await AllAppLevelCredentialsService.getAllApplications();
      const sourceApplications: IAttributeOptions[] =
        result?.data?.data?.map((application: ILooseObject) => ({
          label: application.ui_label,
          value: application.application_key
        })) ?? [];

      setExtraFieldConfig((prev) => ({
        ...prev,
        source_application: {
          ...prev?.source_application,
          options: sourceApplications
        }
      }));
    } catch (err) {
      enqueueSnackbar(err?.toString(), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }
  };

  const handleCreateAppLevelCredentials = async () => {
    const formData = getAppLevelCredentialsValues();

    try {
      const results = await AllAppLevelCredentialsService.createNewAppLevelCredentials({
        payload: formData
      });

      if (results.error.message) {
        enqueueSnackbar(results.error.message, {
          variant: 'error',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
        });
      } else if (!results.error.code) {
        enqueueSnackbar(AppMessages.Success.appLevelCredentialsAdded, {
          variant: 'success',
          autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.success
        });
        setIsOpenDialogue(true);
        SetCredentialData(results.data.data);
      }
    } catch (error) {
      enqueueSnackbar(JSON.stringify(error), {
        variant: 'error',
        autoHideDuration: AppConfig.SNACKBAR_AUTO_HIDE_DURATIONS.error
      });
    }
  };

  useEffect(() => {
    const destinationApp = watchedValues?.destination_application;
    if (destinationApp) {
      getRolesByDestinationApplication(destinationApp);
    }
  }, [watchedValues?.destination_application]);

  useEffect(() => {
    getAllApplicationList();
    if (userDestinationApplicationsOptions?.length > 0) {
      setExtraFieldConfig((preValue) => ({
        ...preValue,
        destination_application: {
          ...preValue?.destination_application,
          options: userDestinationApplicationsOptions
        }
      }));
    }
  }, []);

  if (!getAppPermissions()?.includes('x_api_key_config_view')) {
    return <NoAuthorization />;
  }

  return (
    <Box display="flex" flexDirection="column" rowGap={1}>
      <DynamicBreadCrumbs
        breadCrumbsDetails={[
          { key: 1, name: 'App Level Credentials List', redirectPath: '/app-level-credentials' },
          { key: 2, name: 'Add App Level Credentials' }
        ]}
      />
      <Typography variant="h5" sx={{ my: 2 }} color="primary.dark">
        Add App Level Credentials
      </Typography>
      <Box
        border={1}
        borderColor={lightTheme.palette.grey[300]}
        borderRadius={3}
        sx={{ p: 2, display: 'flex', flexDirection: 'column', rowGap: 1 }}
      >
        <form onSubmit={handleAddAppLevelCredentialsSubmit(handleCreateAppLevelCredentials)}>
          <Grid2 container columnSpacing={6}>
            {AttributesConfig.CREATE_APP_LEVEL_CREDENTIALS_CONFIG_INPUT?.map(
              (attributeField: IAttributeDef) => {
                const type = UI_TYPE_INPUT_TYPE_MAP[attributeField?.ui_type?.toUpperCase()];
                const inputType = INPUT_FIELD_TYPE_MAP[attributeField?.value_type?.toUpperCase()];
                const isVisible = !!attributeField?.is_visible;
                if (!isVisible) return null;

                const AvailableOptions = getAvailableOptions(attributeField, extraFieldConfig);

                return (
                  <Grid2 size={{ xs: 6 }} key={attributeField?.attribute_id}>
                    <Box>
                      <FormattedUILabel
                        uiLabel={attributeField?.ui_label}
                        isRequired={attributeField?.is_mandatory}
                      />
                      <Controller
                        name={attributeField?.name as string}
                        control={addAppLevelCredentialsControl}
                        render={({ field: { onChange, onBlur, value, name } }) => (
                          <DynamicInput
                            type={type}
                            inputType={inputType}
                            id={attributeField?.attribute_id}
                            label=""
                            value={value}
                            placeholder={attributeField?.placeholder}
                            options={AvailableOptions}
                            onChange={(e: React.FormEvent<HTMLInputElement>) => {
                              onChange(e);
                              clearAddAppLevelCredentialsErrors(name);
                            }}
                            helperText={
                              (addAppLevelCredentialsErrors[name]?.message as string) ?? ' '
                            }
                            hasError={!!(addAppLevelCredentialsErrors[name]?.message as string)}
                            onBlur={onBlur}
                            disableClearable={true}
                            disabled={
                              attributeField?.name === 'role_keys' &&
                              !watchedValues?.destination_application
                            }
                          />
                        )}
                      />
                    </Box>
                  </Grid2>
                );
              }
            )}
            <SaveAndCancel
              handleCancel={() => {
                clearAddAppLevelCredentialsErrors();
                resetAddAppLevelCredentials();
              }}
              isLoading={isAddAppLevelCredentialsSubmitting}
              disableSave={isAddAppLevelCredentialsSubmitting || !isAddAppLevelCredentialsDirty}
              disableReset={
                !isAddAppLevelCredentialsDirty &&
                Object.keys(addAppLevelCredentialsErrors).length === 0
              }
            />
          </Grid2>
        </form>
      </Box>
      <CredentialDialogue
        attributeDefinitions={AttributesConfig.CREDENTIAL_ATTRIBUTE_DIALOG_DEFINITIONS}
        copyDownloadFields={AttributesConfig.DEFAULT_COPY_DOWNLOAD_CREDENTIAL_FIELDS}
        credentialData={credentialData}
        onClose={handleCloseCredentialDialogue}
        open={isOpenDialogue}
      />
    </Box>
  );
};

export default AddAppLevelCredentials;
